/**
 * Intelligent Animation & CSS Operations Caching Service
 * 
 * Provides intelligent caching for:
 * - Animation states and timelines
 * - CSS property calculations
 * - Transform matrices
 * - Render layer optimizations
 * - Intersection Observer management
 * - Dynamic style computations
 */

import { logDebug, logInfo, logWarn } from './logger';

// ========================================
// ANIMATION STATE CACHING
// ========================================

interface AnimationState {
  element: HTMLElement;
  transform: string;
  opacity: number;
  filter: string;
  timestamp: number;
  computedStyle?: CSSStyleDeclaration;
}

interface AnimationTimeline {
  keyframes: Keyframe[];
  duration: number;
  easing: string;
  iterations?: number;
  fill?: FillMode;
}

// Caches
const animationStateCache = new Map<string, AnimationState>();
const animationTimelineCache = new Map<string, AnimationTimeline>();
const transformMatrixCache = new Map<string, DOMMatrix>();
const cssPropertyCache = new Map<string, string | number>();
const renderLayerCache = new Set<HTMLElement>();
const intersectionObserverCache = new Map<string, IntersectionObserver>();

// Performance tracking
let animationStats = {
  cacheHits: 0,
  cacheMisses: 0,
  computedStyles: 0,
  transformCalculations: 0,
  layerPromotions: 0,
  observerReuse: 0
};

// ========================================
// ANIMATION STATE MANAGEMENT
// ========================================

/**
 * Cache animation state for reuse
 */
export const cacheAnimationState = (
  elementId: string, 
  element: HTMLElement,
  includeComputedStyle: boolean = false
): void => {
  const computedStyle = includeComputedStyle ? window.getComputedStyle(element) : undefined;
  
  const state: AnimationState = {
    element,
    transform: element.style.transform || computedStyle?.transform || '',
    opacity: parseFloat(element.style.opacity || computedStyle?.opacity || '1'),
    filter: element.style.filter || computedStyle?.filter || '',
    timestamp: performance.now(),
    computedStyle: includeComputedStyle ? computedStyle : undefined
  };
  
  animationStateCache.set(elementId, state);
  logDebug(`Cached animation state for: ${elementId}`);
};

/**
 * Retrieve cached animation state
 */
export const getCachedAnimationState = (elementId: string): AnimationState | null => {
  const state = animationStateCache.get(elementId);
  if (state) {
    animationStats.cacheHits++;
    return state;
  }
  animationStats.cacheMisses++;
  return null;
};

/**
 * Apply cached animation state
 */
export const applyCachedAnimationState = (
  elementId: string, 
  element: HTMLElement,
  skipIfRecent: number = 100
): boolean => {
  const state = getCachedAnimationState(elementId);
  if (!state) return false;
  
  // Skip if state is very recent (avoid redundant applications)
  if (performance.now() - state.timestamp < skipIfRecent) {
    return true;
  }
  
  // Apply cached state
  element.style.transform = state.transform;
  element.style.opacity = state.opacity.toString();
  element.style.filter = state.filter;
  
  logDebug(`Applied cached animation state to: ${elementId}`);
  return true;
};

// ========================================
// ANIMATION TIMELINE CACHING
// ========================================

/**
 * Cache animation timeline for reuse
 */
export const cacheAnimationTimeline = (
  animationName: string,
  timeline: AnimationTimeline
): void => {
  animationTimelineCache.set(animationName, timeline);
  logDebug(`Cached animation timeline: ${animationName}`);
};

/**
 * Create and cache common animation timelines
 */
export const createCommonAnimationTimelines = (): void => {
  // Article card entrance
  cacheAnimationTimeline('articleCardEntrance', {
    keyframes: [
      { transform: 'translateY(20px)', opacity: 0 },
      { transform: 'translateY(0)', opacity: 1 }
    ],
    duration: 800,
    easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    fill: 'forwards'
  });
  
  // Smooth scale hover
  cacheAnimationTimeline('smoothScaleHover', {
    keyframes: [
      { transform: 'scale(1)' },
      { transform: 'scale(1.02)' }
    ],
    duration: 200,
    easing: 'ease-out',
    fill: 'forwards'
  });
  
  // Cyber glow pulse
  cacheAnimationTimeline('cyberGlowPulse', {
    keyframes: [
      { boxShadow: '0 0 20px rgba(0, 255, 65, 0.3)' },
      { boxShadow: '0 0 40px rgba(0, 255, 65, 0.6)' },
      { boxShadow: '0 0 20px rgba(0, 255, 65, 0.3)' }
    ],
    duration: 2000,
    easing: 'ease-in-out',
    iterations: Infinity
  });
  
  // Fade in with transform
  cacheAnimationTimeline('fadeInTransform', {
    keyframes: [
      { opacity: 0, transform: 'translate3d(-20px, 0, 0)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0)' }
    ],
    duration: 600,
    easing: 'ease-out',
    fill: 'forwards'
  });
  
  logInfo('Common animation timelines cached');
};

/**
 * Get cached animation timeline
 */
export const getCachedAnimationTimeline = (animationName: string): AnimationTimeline | null => {
  return animationTimelineCache.get(animationName) || null;
};

/**
 * Apply cached animation timeline
 */
export const applyCachedAnimation = (
  element: HTMLElement,
  animationName: string,
  customOptions?: Partial<AnimationTimeline>
): Animation | null => {
  const timeline = getCachedAnimationTimeline(animationName);
  if (!timeline) {
    logWarn(`Animation timeline not found: ${animationName}`);
    return null;
  }
  
  const options = { ...timeline, ...customOptions };
  
  try {
    const animation = element.animate(options.keyframes, {
      duration: options.duration,
      easing: options.easing,
      iterations: options.iterations,
      fill: options.fill
    });
    
    logDebug(`Applied cached animation: ${animationName}`);
    return animation;
  } catch (error) {
    logWarn(`Failed to apply animation: ${animationName} - ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
};

// ========================================
// TRANSFORM MATRIX CACHING
// ========================================

/**
 * Calculate and cache transform matrix
 */
export const cacheTransformMatrix = (
  transformKey: string,
  transformString: string
): DOMMatrix | null => {
  try {
    // Create a temporary element to compute the matrix
    const tempElement = document.createElement('div');
    tempElement.style.transform = transformString;
    document.body.appendChild(tempElement);
    
    const computedStyle = window.getComputedStyle(tempElement);
    const matrixString = computedStyle.transform;
    
    document.body.removeChild(tempElement);
    
    // Create DOMMatrix from computed matrix string
    let matrix: DOMMatrix;
    if (matrixString === 'none') {
      matrix = new DOMMatrix(); // Identity matrix
    } else {
      // Use fromMatrix or parse the matrix string
      matrix = DOMMatrix.fromMatrix ? DOMMatrix.fromMatrix() : new DOMMatrix();
      // Apply the transform by setting the matrix values manually if needed
      if (matrixString.includes('matrix')) {
        const values = matrixString.match(/-?[\d.]+/g);
        if (values && values.length >= 6) {
          if (values.length === 6) {
            // 2D matrix: matrix(a, b, c, d, e, f)
            matrix.a = parseFloat(values[0]);
            matrix.b = parseFloat(values[1]);
            matrix.c = parseFloat(values[2]);
            matrix.d = parseFloat(values[3]);
            matrix.e = parseFloat(values[4]);
            matrix.f = parseFloat(values[5]);
          }
        }
      }
    }
    
    transformMatrixCache.set(transformKey, matrix);
    animationStats.transformCalculations++;
    logDebug(`Cached transform matrix: ${transformKey}`);
    return matrix;
  } catch (error) {
    logWarn(`Failed to parse transform: ${transformString} - ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
};

/**
 * Get cached transform matrix
 */
export const getCachedTransformMatrix = (transformKey: string): DOMMatrix | null => {
  const matrix = transformMatrixCache.get(transformKey);
  if (matrix) {
    animationStats.cacheHits++;
    return matrix;
  }
  animationStats.cacheMisses++;
  return null;
};

/**
 * Pre-calculate common transform matrices
 */
export const preCalculateCommonTransforms = (): void => {
  const commonTransforms = [
    ['identity', ''],
    ['scale-1.02', 'scale3d(1.02, 1.02, 1.02)'],
    ['scale-1.05', 'scale3d(1.05, 1.05, 1.05)'],
    ['translate-up-20', 'translate3d(0, -20px, 0)'],
    ['translate-down-20', 'translate3d(0, 20px, 0)'],
    ['rotate-5', 'rotateZ(5deg)'],
    ['rotate-10', 'rotateZ(10deg)'],
    ['perspective-1000', 'perspective(1000px)']
  ];
  
  commonTransforms.forEach(([key, transform]) => {
    if (key === 'identity') {
      // Create identity matrix directly
      const matrix = new DOMMatrix();
      transformMatrixCache.set(key, matrix);
      animationStats.transformCalculations++;
    } else {
      cacheTransformMatrix(key, transform);
    }
  });
  
  logInfo(`Pre-calculated ${commonTransforms.length} common transforms`);
};

// ========================================
// CSS PROPERTY CACHING
// ========================================

/**
 * Cache expensive CSS property calculations
 */
export const cacheCSSProperty = (
  propertyKey: string,
  value: string | number
): void => {
  cssPropertyCache.set(propertyKey, value);
};

/**
 * Get cached CSS property
 */
export const getCachedCSSProperty = (propertyKey: string): string | number | null => {
  return cssPropertyCache.get(propertyKey) || null;
};

/**
 * Cache computed styles for elements
 */
export const cacheComputedStyles = (
  element: HTMLElement,
  properties: string[]
): Map<string, string> => {
  const computedStyle = window.getComputedStyle(element);
  const cached = new Map<string, string>();
  
  properties.forEach(prop => {
    const value = computedStyle.getPropertyValue(prop);
    const cacheKey = `${element.id || element.className}-${prop}`;
    cacheCSSProperty(cacheKey, value);
    cached.set(prop, value);
  });
  
  animationStats.computedStyles++;
  return cached;
};

// ========================================
// RENDER LAYER OPTIMIZATION
// ========================================

/**
 * Promote element to render layer for animations
 */
export const promoteToRenderLayer = (
  element: HTMLElement,
  temporary: boolean = false
): void => {
  if (renderLayerCache.has(element)) {
    return; // Already promoted
  }
  
  // Apply layer promotion styles
  element.style.willChange = 'transform, opacity';
  element.style.transform = element.style.transform || 'translate3d(0, 0, 0)';
  element.style.backfaceVisibility = 'hidden';
  
  renderLayerCache.add(element);
  animationStats.layerPromotions++;
  
  logDebug('Promoted element to render layer');
  
  // Auto-demote if temporary
  if (temporary) {
    setTimeout(() => {
      demoteFromRenderLayer(element);
    }, 5000); // 5 seconds
  }
};

/**
 * Demote element from render layer
 */
export const demoteFromRenderLayer = (element: HTMLElement): void => {
  if (!renderLayerCache.has(element)) {
    return;
  }
  
  element.style.willChange = 'auto';
  renderLayerCache.delete(element);
  
  logDebug('Demoted element from render layer');
};

/**
 * Batch promote elements for animations
 */
export const batchPromoteElements = (
  elements: HTMLElement[],
  temporary: boolean = true
): void => {
  requestAnimationFrame(() => {
    elements.forEach(element => {
      promoteToRenderLayer(element, temporary);
    });
  });
};

// ========================================
// INTERSECTION OBSERVER OPTIMIZATION
// ========================================

/**
 * Create or reuse intersection observer
 */
export const getIntersectionObserver = (
  configKey: string,
  callback: IntersectionObserverCallback,
  options?: IntersectionObserverInit
): IntersectionObserver => {
  const cacheKey = `${configKey}-${JSON.stringify(options)}`;
  
  if (intersectionObserverCache.has(cacheKey)) {
    animationStats.observerReuse++;
    return intersectionObserverCache.get(cacheKey)!;
  }
  
  const observer = new IntersectionObserver(callback, options);
  intersectionObserverCache.set(cacheKey, observer);
  
  logDebug(`Created new intersection observer: ${configKey}`);
  return observer;
};

/**
 * Smart animation trigger using cached observers
 */
export const setupSmartAnimationTrigger = (
  elements: HTMLElement[],
  animationName: string,
  options?: {
    threshold?: number;
    rootMargin?: string;
    once?: boolean;
  }
): IntersectionObserver => {
  const { threshold = 0.1, rootMargin = '0px', once = true } = options || {};
  
  const callback: IntersectionObserverCallback = (entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target as HTMLElement;
        
        // Promote to render layer temporarily
        promoteToRenderLayer(element, true);
        
        // Apply cached animation
        applyCachedAnimation(element, animationName);
        
        // Cache the final state
        setTimeout(() => {
          cacheAnimationState(element.id || element.className, element);
        }, 100);
        
        // Unobserve if once
        if (once) {
          observer.unobserve(element);
        }
      }
    });
  };
  
  const observer = getIntersectionObserver(
    `animation-trigger-${animationName}`,
    callback,
    { threshold, rootMargin }
  );
  
  elements.forEach(element => {
    observer.observe(element);
  });
  
  return observer;
};

// ========================================
// PERFORMANCE MONITORING
// ========================================

/**
 * Get animation cache performance stats
 */
export const getAnimationCacheStats = () => {
  const totalRequests = animationStats.cacheHits + animationStats.cacheMisses;
  const hitRate = totalRequests > 0 ? (animationStats.cacheHits / totalRequests * 100) : 0;
  
  return {
    ...animationStats,
    totalRequests,
    hitRate: `${hitRate.toFixed(1)}%`,
    cachedStates: animationStateCache.size,
    cachedTimelines: animationTimelineCache.size,
    cachedTransforms: transformMatrixCache.size,
    cachedProperties: cssPropertyCache.size,
    activeObservers: intersectionObserverCache.size,
    promotedLayers: renderLayerCache.size
  };
};

/**
 * Clear all caches
 */
export const clearAnimationCaches = (): void => {
  animationStateCache.clear();
  animationTimelineCache.clear();
  transformMatrixCache.clear();
  cssPropertyCache.clear();
  
  // Demote all render layers
  renderLayerCache.forEach(element => {
    element.style.willChange = 'auto';
  });
  renderLayerCache.clear();
  
  // Disconnect all observers
  intersectionObserverCache.forEach(observer => {
    observer.disconnect();
  });
  intersectionObserverCache.clear();
  
  // Reset stats
  animationStats = {
    cacheHits: 0,
    cacheMisses: 0,
    computedStyles: 0,
    transformCalculations: 0,
    layerPromotions: 0,
    observerReuse: 0
  };
  
  logInfo('All animation caches cleared');
};

/**
 * Initialize animation caching system
 */
export const initializeAnimationCaching = (): void => {
  createCommonAnimationTimelines();
  preCalculateCommonTransforms();
  
  logInfo('Animation caching system initialized');
};

/**
 * Debug animation performance
 */
export const debugAnimationPerformance = (): void => {
  console.log('🎬 Animation Cache Performance Report');
  console.log('====================================');
  
  const stats = getAnimationCacheStats();
  console.log('📊 Cache Stats:', stats);
  
  console.log('\n💾 Memory Usage:');
  console.log(`  Cached animation states: ${stats.cachedStates}`);
  console.log(`  Cached timelines: ${stats.cachedTimelines}`);
  console.log(`  Cached transforms: ${stats.cachedTransforms}`);
  console.log(`  Cached CSS properties: ${stats.cachedProperties}`);
  
  console.log('\n⚡ Performance:');
  console.log(`  Cache hit rate: ${stats.hitRate}`);
  console.log(`  Observer reuse: ${stats.observerReuse}`);
  console.log(`  Layer promotions: ${stats.layerPromotions}`);
  
  console.log('\n🔍 Active Resources:');
  console.log(`  Active observers: ${stats.activeObservers}`);
  console.log(`  Promoted layers: ${stats.promotedLayers}`);
};

// Auto-initialize on import
if (typeof window !== 'undefined') {
  initializeAnimationCaching();
} 